import React, { useState, useEffect, useCallback } from 'react';
import { Search, X, Eye } from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import { fetchMathConcepts as fetchMathConceptsUtil, getConceptOrder as getConceptOrderUtil, getConceptById as getConceptByIdUtil } from '../../utils/mathConceptsUtils';
import ProblemPreview from '../ProblemPreview';
import { createEditor } from 'lexical';
import { $generateHtmlFromNodes } from '@lexical/html';
import PlaygroundNodes from '../../editor/nodes/PlaygroundNodes';
import { processMathEquation } from '../utils/latexParser';
import katex from 'katex';
import 'katex/dist/katex.min.css';
import { Modal, Button } from '../common';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || '';

// concept_type에 따른 색상 매핑 (무지개색 50% 투명도)
const getConceptTypeColor = (type) => {
  const colors = {
    'DEF': 'rgba(239, 68, 68, 0.5)',   // 빨강 (정의)
    'AXM': 'rgba(249, 115, 22, 0.5)',  // 주황 (공리)
    'IDN': 'rgba(245, 158, 11, 0.5)',  // 노랑 (정리)
    'THM': 'rgba(34, 197, 94, 0.5)',   // 초록 (이론)
    'MTH': 'rgba(6, 182, 212, 0.5)',   // 하늘 (방법론)
    'PRP': 'rgba(59, 130, 246, 0.5)',  // 파랑 (성질)
    'FOR': 'rgba(139, 92, 246, 0.5)',  // 보라 (공식)
    'NTN': 'rgba(236, 72, 153, 0.5)'   // 분홍 (표기법)
  };
  return colors[type] || 'rgba(156, 163, 175, 0.5)'; // 기본 회색
};

// concept_type 한글 이름 매핑
const getConceptTypeName = (type) => {
  const names = {
    'DEF': '정의',
    'AXM': '공리',
    'IDN': '정리',
    'THM': '이론',
    'MTH': '방법론',
    'PRP': '성질',
    'FOR': '공식',
    'NTN': '표기법'
  };
  return names[type] || type;
};

function CreateProblemModal({ isOpen, onClose, onSuccess, initialProblemId = null, initialMode = 'prob_to_prob' }) {
  const { getAccessToken } = useAuth();
  const [mathConcepts, setMathConcepts] = useState([]);
  const [isLoadingMathConcepts, setIsLoadingMathConcepts] = useState(false);
  const [activeTab, setActiveTab] = useState(initialMode);
  const [userPrompt, setUserPrompt] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  // 문제→문제 관련 상태
  const [problemSearch, setProblemSearch] = useState('');
  const [selectedProblem, setSelectedProblem] = useState(null);
  const [selectedProblemContent, setSelectedProblemContent] = useState(''); // 문제 내용 HTML
  const [problemList, setProblemList] = useState([]);
  const [problemContents, setProblemContents] = useState({}); // 문제별 내용 캐시
  const [loadingContents, setLoadingContents] = useState({}); // 로딩 중인 문제 ID
  const [isSearching, setIsSearching] = useState(false);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [showPreview, setShowPreview] = useState(false);
  const [previewProblemIds, setPreviewProblemIds] = useState([]);

  // 개념→문제 관련 상태
  const [selectedConcepts, setSelectedConcepts] = useState([]);
  const [conceptSearch, setConceptSearch] = useState('');

  // 문제 분석 옵션 선택 상태
  const [analysisOptions, setAnalysisOptions] = useState({
    solution: true,
    answer: true,
    concept_ids: true,
    difficulty: true
  });

  // LaTeX 수식 렌더링 함수
  const renderLatex = (text) => {
    try {
      // $...$ 패턴을 찾아서 LaTeX로 렌더링
      const parts = text.split(/(\$[^$]+\$)/g);
      return parts.map((part, index) => {
        if (part.startsWith('$') && part.endsWith('$')) {
          const formula = part.slice(1, -1);
          try {
            return katex.renderToString(formula, {
              throwOnError: false,
              displayMode: false
            });
          } catch (e) {
            return part;
          }
        }
        return part;
      }).join('');
    } catch (error) {
      return text;
    }
  };

  // HTML을 텍스트로 변환하고 일부만 추출하는 함수
  const extractTextPreview = (htmlContent, maxLength = 100) => {
    if (!htmlContent) return '';
    
    // 임시 div 엘리먼트 생성하여 HTML 파싱
    const tempDiv = document.createElement('div');
    tempDiv.innerHTML = htmlContent;
    
    // 텍스트만 추출
    const textContent = tempDiv.textContent || tempDiv.innerText || '';
    
    // 줄바꿈과 공백 정리
    const cleanedText = textContent.replace(/\s+/g, ' ').trim();
    
    // 길이 제한
    if (cleanedText.length > maxLength) {
      return cleanedText.substring(0, maxLength) + '...';
    }
    
    return cleanedText;
  };

  // Lexical 데이터를 HTML로 변환하는 함수
  const convertLexicalToHtml = useCallback((lexicalData) => {
    const editor = createEditor({
      nodes: [...PlaygroundNodes]
    });

    const processNode = (node) => {
      if (node.type === 'equation') {
        node.equation = processMathEquation(node.equation);
      } else if (node.type === 'box-content') {
        node.className = 'PlaygroundEditorTheme__boxContent';
      }
      if (node.children && Array.isArray(node.children)) {
        node.children = node.children.map(child => processNode(child));
      }
      return node;
    };

    lexicalData.root = processNode(lexicalData.root);
    const editorState = editor.parseEditorState(lexicalData);

    let content = '';
    editor.setEditorState(editorState);
    editor.update(() => {
      content = $generateHtmlFromNodes(editor);
    });

    return content;
  }, []);



  // Math Concepts 로드
  useEffect(() => {
    const loadMathConcepts = async () => {
      if (!isOpen) return;
      
      setIsLoadingMathConcepts(true);
      try {
        const concepts = await fetchMathConceptsUtil();
        setMathConcepts(concepts);
      } catch (error) {
        console.error('Error loading math concepts:', error);
      } finally {
        setIsLoadingMathConcepts(false);
      }
    };
    
    loadMathConcepts();
  }, [isOpen]);

  // Helper 함수들
  const getConceptOrder = (conceptId) => getConceptOrderUtil(mathConcepts, conceptId);
  const getConceptById = (conceptId) => getConceptByIdUtil(mathConcepts, conceptId);

  // 문제 목록 조회
  const fetchProblemList = useCallback(async (page = 1, append = false) => {
    if (append) {
      setIsLoadingMore(true);
    } else {
      setIsSearching(true);
    }
    
    try {
      const token = await getAccessToken();
      const params = new URLSearchParams({
        page: page,
        per_page: 3,
        status: '999' // 완성된 문제만
      });

      if (problemSearch && problemSearch.trim()) {
        // 검색어가 숫자가 아닌 경우만 라벨 검색 파라미터 추가
        if (isNaN(problemSearch)) {
          params.append('labels', problemSearch.trim());
        }
      }

      const response = await fetch(
        `${API_BASE_URL}/api/problems/filtered?${params}`,
        {
          headers: {
            'Authorization': token,
            'Content-Type': 'application/json',
          }
        }
      );

      if (!response.ok) {
        throw new Error('문제 목록을 불러오는데 실패했습니다.');
      }

      const result = await response.json();
      
      if (result.status === 'success') {
        const newData = result.data || [];
        
        if (append) {
          setProblemList(prev => [...prev, ...newData]);
        } else {
          setProblemList(newData);
        }
        setCurrentPage(page);
        setTotalPages(result.total_pages || 1);
        
        // print API를 사용해서 문제 내용들을 한 번에 로드
        if (newData.length > 0) {
          const loadContents = async () => {
            try {
              const problemIds = newData.map(p => p.problem_id).join(',');
              const printResponse = await fetch(
                `${API_BASE_URL}/api/problems/print?problem_ids=${problemIds}`,
                {
                  headers: {
                    'Authorization': token,
                    'Content-Type': 'application/json',
                  }
                }
              );
              
              if (printResponse.ok) {
                const printResult = await printResponse.json();
                if (printResult.status === 'success' && printResult.data) {
                  const newContents = {};
                  printResult.data.forEach(problem => {
                    if (problem.lexical_data?.lexical_data) {
                      const htmlContent = convertLexicalToHtml(problem.lexical_data.lexical_data);
                      newContents[problem.problem_id] = htmlContent;
                    }
                  });
                  setProblemContents(prev => ({ ...prev, ...newContents }));
                }
              }
            } catch (error) {
              console.error('Error loading problem contents:', error);
            }
          };
          
          loadContents();
        }
      }
    } catch (error) {
      console.error('Error fetching problems:', error);
      if (!append) {
        setProblemList([]);
      }
    } finally {
      setIsSearching(false);
      setIsLoadingMore(false);
    }
  }, [problemSearch, getAccessToken, convertLexicalToHtml]);

  // 개별 문제 내용 로드 함수 (print API 사용)
  const loadProblemContent = useCallback(async (problemId) => {
    setLoadingContents(prev => ({ ...prev, [problemId]: true }));
    
    try {
      const token = await getAccessToken();
      const response = await fetch(
        `${API_BASE_URL}/api/problems/print?problem_ids=${problemId}`,
        {
          headers: {
            'Authorization': token,
            'Content-Type': 'application/json',
          }
        }
      );
      
      if (response.ok) {
        const result = await response.json();
        if (result.status === 'success' && result.data && result.data.length > 0) {
          const problem = result.data[0];
          if (problem.lexical_data?.lexical_data) {
            const htmlContent = convertLexicalToHtml(problem.lexical_data.lexical_data);
            setProblemContents(prev => ({ ...prev, [problemId]: htmlContent }));
          }
        }
      }
    } catch (error) {
      console.error(`Error loading content for problem ${problemId}:`, error);
    } finally {
      setLoadingContents(prev => {
        const newState = { ...prev };
        delete newState[problemId];
        return newState;
      });
    }
  }, [getAccessToken, convertLexicalToHtml]);

  // 문제 검색 (ID로 검색)
  const searchProblemById = useCallback(async () => {
    if (!problemSearch) {
      fetchProblemList(1, false);
      return;
    }

    setIsSearching(true);
    try {
      const token = await getAccessToken();
      const response = await fetch(
        `${API_BASE_URL}/api/problems/print?problem_ids=${problemSearch}`,
        {
          headers: {
            'Authorization': token,
            'Content-Type': 'application/json',
          }
        }
      );

      if (!response.ok) {
        // ID로 찾지 못하면 라벨 검색으로 전환
        fetchProblemList(1, false);
        return;
      }

      const result = await response.json();
      if (result.status === 'success' && result.data && result.data.length > 0) {
        const problem = result.data[0];
        setSelectedProblem(problem);
        // Lexical 데이터를 HTML로 변환
        if (problem.lexical_data?.lexical_data) {
          const htmlContent = convertLexicalToHtml(problem.lexical_data.lexical_data);
          setSelectedProblemContent(htmlContent);
        }
        setProblemList([]);
        setCurrentPage(1);
        setTotalPages(1);
      }
    } catch (error) {
      console.error('Error searching problem by ID:', error);
      // 에러 발생 시 라벨 검색으로 전환
      fetchProblemList(1, false);
    } finally {
      setIsSearching(false);
    }
  }, [problemSearch, getAccessToken, fetchProblemList, convertLexicalToHtml]);

  // 문제 선택 핸들러
  const handleProblemSelect = useCallback(async (problem) => {
    // 먼저 기본 정보 설정
    setSelectedProblem({
      problem_id: problem.problem_id,
      problem_info: {
        author: problem.author,
        labels: problem.labels,
        status: problem.status
      }
    });
    setProblemList([]);

    // print API로 상세 정보와 lexical 데이터 가져오기
    try {
      const token = await getAccessToken();
      const response = await fetch(
        `${API_BASE_URL}/api/problems/print?problem_ids=${problem.problem_id}`,
        {
          headers: {
            'Authorization': token,
            'Content-Type': 'application/json',
          }
        }
      );

      if (response.ok) {
        const result = await response.json();
        if (result.status === 'success' && result.data && result.data.length > 0) {
          const problemData = result.data[0];
          setSelectedProblem(problemData);
          // Lexical 데이터를 HTML로 변환
          if (problemData.lexical_data?.lexical_data) {
            const htmlContent = convertLexicalToHtml(problemData.lexical_data.lexical_data);
            setSelectedProblemContent(htmlContent);
          }
        }
      }
    } catch (error) {
      console.error('Error fetching problem details:', error);
    }
  }, [getAccessToken, convertLexicalToHtml]);

  // 미리보기 핸들러
  const handlePreview = (problemId) => {
    setPreviewProblemIds([problemId]);
    setShowPreview(true);
  };

  // 미리보기에서 문제 선택 핸들러 추가
  const handlePreviewSelect = useCallback((selectedProblem) => {
    handleProblemSelect(selectedProblem);
  }, [handleProblemSelect]);

  // 인피니티 스크롤 핸들러 (디바운싱 적용)
  const handleScroll = useCallback((e) => {
    const { scrollTop, scrollHeight, clientHeight } = e.target;
    const isNearBottom = scrollHeight - scrollTop <= clientHeight + 100;
    
    if (isNearBottom && 
        !isSearching && 
        !isLoadingMore &&
        currentPage < totalPages && 
        problemList.length > 0) {
      fetchProblemList(currentPage + 1, true);
    }
  }, [isSearching, isLoadingMore, currentPage, totalPages, problemList.length, fetchProblemList]);

  // 개념 선택/해제
  const toggleConcept = (conceptId) => {
    setSelectedConcepts(prev => {
      if (prev.includes(conceptId)) {
        return prev.filter(id => id !== conceptId);
      } else if (prev.length < 10) {
        return [...prev, conceptId];
      } else {
        alert('최대 10개까지 선택 가능합니다.');
        return prev;
      }
    });
  };

  // 제출 처리
  const handleSubmit = async () => {
    // 유효성 검사
    if (!userPrompt || userPrompt.length < 1) {
      alert('프롬프트를 입력해주세요.');
      return;
    }

    if (userPrompt.length > 500) {
      alert('프롬프트는 500자 이내로 입력해주세요.');
      return;
    }

    if (activeTab === 'prob_to_prob' || activeTab === 'prob_analysis') {
      if (!selectedProblem) {
        alert('기반이 될 문제를 선택해주세요.');
        return;
      }
      
      // 문제 분석 탭에서는 최소 하나 이상의 분석 옵션 선택 필요
      if (activeTab === 'prob_analysis') {
        const selectedAnalysisTypes = Object.keys(analysisOptions).filter(key => analysisOptions[key]);
        if (selectedAnalysisTypes.length === 0) {
          alert('최소 하나 이상의 분석 데이터 종류를 선택해주세요.');
          return;
        }
      }
    } else {
      if (selectedConcepts.length === 0) {
        alert('하나 이상의 개념을 선택해주세요.');
        return;
      }
    }

    setIsSubmitting(true);
    try {
      const token = await getAccessToken();
      let endpoint, body;

      if (activeTab === 'prob_to_prob') {
        endpoint = '/api/probagent/prob-to-prob';
        body = {
          problem_id: selectedProblem.problem_id,
          user_prompt: userPrompt
        };
      } else if (activeTab === 'prob_analysis') {
        endpoint = '/api/probagent/prob-analysis';
        const selectedAnalysisTypes = Object.keys(analysisOptions).filter(key => analysisOptions[key]);
        body = {
          problem_id: selectedProblem.problem_id,
          user_prompt: userPrompt,
          options: selectedAnalysisTypes
        };
      } else {
        endpoint = '/api/probagent/concept-to-prob';
        body = {
          concept_ids: selectedConcepts.join(','),
          user_prompt: userPrompt
        };
      }

      const response = await fetch(`${API_BASE_URL}${endpoint}`, {
        method: 'POST',
        headers: {
          'Authorization': token,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(body)
      });

      if (!response.ok) {
        throw new Error('문제 생성 요청에 실패했습니다.');
      }

      const result = await response.json();
      if (result.status === 'success') {
        alert(activeTab === 'prob_analysis' ? '문제 분석 작업이 시작되었습니다.' : '문제 생성 작업이 시작되었습니다.');
        onSuccess();
        handleClose();
      }
    } catch (error) {
      console.error('Error creating problem:', error);
      alert('문제 생성 요청에 실패했습니다.');
    } finally {
      setIsSubmitting(false);
    }
  };

  // 모달 닫기 및 초기화
  const handleClose = () => {
    setActiveTab(initialMode); // 초기 모드로 리셋
    setUserPrompt('');
    setSelectedProblem(null);
    setSelectedProblemContent('');
    setProblemSearch('');
    setProblemList([]);
    setProblemContents({});
    setLoadingContents({});
    setSelectedConcepts([]);
    setConceptSearch('');
    setCurrentPage(1);
    setTotalPages(1);
    onClose();
  };

  // 초기 문제 ID가 있을 때 처리
  useEffect(() => {
    if (isOpen && initialProblemId) {
      setActiveTab('prob_to_prob'); // 문제 ID가 있으면 문제→문제 탭으로
      
      // 직접 문제 정보를 가져와서 선택
      const fetchInitialProblem = async () => {
        try {
          const token = await getAccessToken();
          const response = await fetch(
            `${API_BASE_URL}/api/problems/print?problem_ids=${initialProblemId}`,
            {
              headers: {
                'Authorization': token,
                'Content-Type': 'application/json',
              }
            }
          );

          if (response.ok) {
            const result = await response.json();
            if (result.status === 'success' && result.data && result.data.length > 0) {
              const problemData = result.data[0];
              setSelectedProblem(problemData);
              // Lexical 데이터를 HTML로 변환
              if (problemData.lexical_data?.lexical_data) {
                const htmlContent = convertLexicalToHtml(problemData.lexical_data.lexical_data);
                setSelectedProblemContent(htmlContent);
              }
            }
          }
        } catch (error) {
          console.error('Error fetching initial problem:', error);
        }
      };
      
      fetchInitialProblem();
    }
  }, [isOpen, initialProblemId, getAccessToken, convertLexicalToHtml]);

  // 컴포넌트 마운트 시 문제 목록 로드
  useEffect(() => {
    if (isOpen) {
      if ((activeTab === 'prob_to_prob' || activeTab === 'prob_analysis') && !selectedProblem && !initialProblemId) {
        fetchProblemList(1, false);
      }
    }
  }, [isOpen, activeTab, selectedProblem, initialProblemId, fetchProblemList]);

  // 선택된 문제가 제거되면 다시 문제 리스트 보여주기
  useEffect(() => {
    if (isOpen && (activeTab === 'prob_to_prob' || activeTab === 'prob_analysis') && !selectedProblem) {
      fetchProblemList(1, false);
    }
  }, [isOpen, activeTab, selectedProblem]);

  // 검색어가 변경되면 목록 초기화 (디바운스 적용)
  useEffect(() => {
    if (isOpen && (activeTab === 'prob_to_prob' || activeTab === 'prob_analysis') && !selectedProblem) {
      const timer = setTimeout(() => {
        setCurrentPage(1);
        setProblemList([]);
        
        const trimmedSearch = problemSearch?.trim();
        
        if (!trimmedSearch) {
          // 검색어가 비어있으면 전체 목록 로드
          fetchProblemList(1, false);
        } else if (!isNaN(trimmedSearch)) {
          // 검색어가 숫자면 ID 검색
          searchProblemById();
        } else {
          // 검색어가 텍스트면 라벨 검색
          fetchProblemList(1, false);
        }
      }, 300); // 300ms 디바운스

      return () => clearTimeout(timer);
    }
  }, [problemSearch]);

  // 필터링된 개념 목록
  const filteredConcepts = mathConcepts.filter(concept =>
    concept.text.toLowerCase().includes(conceptSearch.toLowerCase())
  );
  
  // 디버깅: 첫 번째 개념 데이터 확인
  useEffect(() => {
    if (mathConcepts.length > 0) {
      console.log('Sample concept data:', mathConcepts[0]);
      console.log('Available fields:', Object.keys(mathConcepts[0]));
    }
  }, [mathConcepts]);

  return (
    <>
      <Modal
        isOpen={isOpen}
        onClose={handleClose}
        title="새 문제 생성"
        size="full"
        preventScroll={true}
        zIndex={9999900}
        isMobileView={window.innerWidth < 768}
        bodyClassName="p-0 overflow-y-auto"
        className="max-w-[1200px]"
      >
        <div className="flex h-full flex-col">
          {/* 탭 */}
          <div className="flex shrink-0 overflow-x-auto border-b">
            <button
              onClick={() => setActiveTab('prob_to_prob')}
              className={`whitespace-nowrap px-4 py-3 text-sm font-medium lg:px-6 lg:text-base ${
                activeTab === 'prob_to_prob'
                  ? 'border-b-2 border-primary text-primary'
                  : 'text-gray-500 hover:text-gray-700'
              }`}
            >
              기존 문제 변형
            </button>
            <button
              onClick={() => setActiveTab('concept_to_prob')}
              className={`whitespace-nowrap px-4 py-3 text-sm font-medium lg:px-6 lg:text-base ${
                activeTab === 'concept_to_prob'
                  ? 'border-b-2 border-primary text-primary'
                  : 'text-gray-500 hover:text-gray-700'
              }`}
            >
              수학 개념으로 생성
            </button>
            <button
              onClick={() => setActiveTab('prob_analysis')}
              className={`whitespace-nowrap px-4 py-3 text-sm font-medium lg:px-6 lg:text-base ${
                activeTab === 'prob_analysis'
                  ? 'border-b-2 border-primary text-primary'
                  : 'text-gray-500 hover:text-gray-700'
              }`}
            >
              문제 분석
            </button>
          </div>

          {/* 컨텐츠 - 모바일: 상하 분할, 데스크톱: 좌우 분할 */}
          <div className="flex min-h-0 flex-1 flex-col lg:flex-row">
            {/* 왼쪽/상단: 리스트 영역 */}
            <div className="flex w-full flex-col overflow-hidden py-4 lg:w-2/3 lg:border-r lg:py-6">
              {(activeTab === 'prob_to_prob' || activeTab === 'prob_analysis') ? (
                <div className="flex h-full max-h-full flex-col">
                  {selectedProblem ? (
                    <div className="space-y-4 rounded border bg-gray-50 p-4">
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <p className="text-sm font-medium lg:text-base">문제 ID: {selectedProblem.problem_id}</p>
                          <p className="text-xs text-gray-600 lg:text-sm">작성자: {selectedProblem.problem_info?.author}</p>
                          <p className="text-xs text-gray-600 lg:text-sm">라벨: {selectedProblem.problem_info?.labels || '없음'}</p>
                          <p className="text-xs text-gray-600 lg:text-sm">상태: {selectedProblem.problem_info?.status}</p>
                        </div>
                        <div className="ml-2 flex gap-2">
                          <button
                            onClick={() => handlePreview(selectedProblem.problem_id)}
                            className="text-blue-600 hover:text-blue-800"
                            title="전체 화면으로 보기"
                          >
                            <Eye className="size-5" />
                          </button>
                          <button
                            onClick={() => {
                              setSelectedProblem(null);
                              setSelectedProblemContent('');
                            }}
                            className="text-red-600 hover:text-red-800"
                          >
                            <X className="size-5" />
                          </button>
                        </div>
                      </div>
                      
                      {/* 문제 내용 미리보기 */}
                      {selectedProblemContent && (
                        <div className="mt-4 rounded border bg-white p-4">
                          <h4 className="mb-2 text-xs font-medium text-gray-700 lg:text-sm">문제 내용</h4>
                          <div 
                            className="problem-content text-sm lg:text-base"
                            dangerouslySetInnerHTML={{ __html: selectedProblemContent }}
                          />
                        </div>
                      )}
                    </div>
                  ) : (
                    /* 문제 리스트 */
                    <div className="flex h-full flex-col">
                      <div className="mb-3 shrink-0">
                        <input
                          type="text"
                          placeholder="문제 ID 또는 라벨로 검색..."
                          value={problemSearch}
                          onChange={(e) => setProblemSearch(e.target.value)}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter') {
                              e.preventDefault();
                              // 엔터 키 시 즉시 검색 실행
                              const trimmedSearch = problemSearch?.trim();
                              setCurrentPage(1);
                              setProblemList([]);
                              
                              if (!trimmedSearch) {
                                fetchProblemList(1, false);
                              } else if (!isNaN(trimmedSearch)) {
                                searchProblemById();
                              } else {
                                fetchProblemList(1, false);
                              }
                            }
                          }}
                          className="w-full rounded border p-2 text-sm"
                        />
                      </div>
                      {isSearching && currentPage === 1 ? (
                        <div className="shrink-0 py-4 text-center">로딩 중...</div>
                      ) : problemList.length > 0 ? (
                        <div 
                          className="space-y-3 overflow-y-auto bg-gray-50 p-2" 
                          onScroll={handleScroll}
                          style={{ height: '400px', minHeight: '400px' }}
                        >
                          {problemList.map(problem => {
                            const htmlContent = problemContents[problem.problem_id];
                            const isLoading = loadingContents[problem.problem_id];
                            
                            return (
                              <div
                                key={problem.problem_id}
                                className="shrink-0 cursor-pointer rounded-3xl border border-gray-200 bg-white p-4 shadow-lg transition-all duration-200 hover:shadow-xl"
                                onClick={() => handleProblemSelect(problem)}
                              >
                                {/* 상단 정보 영역 */}
                                <div className="mb-3 flex items-start justify-between">
                                  <div className="min-w-0 flex-1">
                                    <div className="mb-1 flex flex-wrap items-center gap-2">
                                      <div className="shrink-0 rounded-3xl bg-blue-100 px-2 py-1 text-xs font-bold text-blue-800">
                                        <span className="font-normal">PID:</span> {problem.problem_id}
                                      </div>
                                      <div className="text-xs text-gray-600">
                                        <span className="font-medium">작성자:</span> {problem.author}
                                      </div>
                                    </div>
                                    <div className="text-xs text-gray-600">
                                      <span className="font-medium">라벨:</span> {problem.labels || '없음'}
                                    </div>
                                  </div>
                                  <button
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      handlePreview(problem.problem_id);
                                    }}
                                    className="ml-2 shrink-0 p-1.5 text-blue-600 hover:text-blue-800"
                                    title="전체 화면으로 보기"
                                  >
                                    <Eye className="size-4" />
                                  </button>
                                </div>
                                
                                {/* 문제 내용 미리보기 영역 */}
                                {htmlContent ? (
                                  <div className="relative mt-3 rounded-lg bg-gray-50 p-3">
                                    <div 
                                      className="problem-preview-content text-sm"
                                      dangerouslySetInnerHTML={{ __html: htmlContent }}
                                      style={{
                                        maxHeight: '120px',
                                        overflow: 'hidden'
                                      }}
                                    />
                                    <div 
                                      className="absolute bottom-0 left-0 right-0 h-8 rounded-b-lg bg-gradient-to-t from-gray-50 to-transparent"
                                      style={{ pointerEvents: 'none' }}
                                    />
                                  </div>
                                ) : (
                                  <div className="mt-3 rounded-lg bg-gray-50 p-3">
                                    <div className="flex h-[120px] items-center justify-center text-sm text-gray-500">
                                      문제 내용을 불러오는 중...
                                    </div>
                                  </div>
                                )}
                              </div>
                            );
                          })}
                          {isLoadingMore && (
                            <div className="shrink-0 py-3 text-center text-sm text-gray-500">
                              더 많은 문제를 불러오는 중...
                            </div>
                          )}
                          {currentPage >= totalPages && problemList.length > 0 && (
                            <div className="shrink-0 py-3 text-center text-sm text-gray-400">
                              모든 문제를 불러왔습니다.
                            </div>
                          )}
                        </div>
                      ) : (
                        <div className="shrink-0 py-4 text-center text-gray-500">
                          {problemSearch?.trim() ? 
                            `"${problemSearch.trim()}"에 대한 검색 결과가 없습니다.` : 
                            '문제가 없습니다.'
                          }
                        </div>
                      )}
                    </div>
                  )}
                </div>
              ) : (
                <div className="flex h-full max-h-full flex-col">
                  <div className="mb-4 shrink-0">
                    <label className="mb-2 block text-xs font-medium text-gray-700 lg:text-sm">
                      수학 개념 선택 (최대 10개)
                    </label>
                    <input
                      type="text"
                      placeholder="개념 검색..."
                      value={conceptSearch}
                      onChange={(e) => setConceptSearch(e.target.value)}
                      className="mb-3 w-full rounded border p-2 text-sm"
                    />
                    <div className="mb-3 text-xs text-gray-600 lg:text-sm">
                      선택된 개념: {selectedConcepts.length}/10
                    </div>
                    
                    {/* 선택된 개념 태그 표시 */}
                    {selectedConcepts.length > 0 && (
                      <div className="mb-3 rounded-3xl bg-gray-50 p-3">
                        <div className="flex flex-wrap gap-2">
                          {selectedConcepts.map(conceptId => {
                            const concept = mathConcepts.find(c => c.id === conceptId);
                            if (!concept) return null;
                            
                            return (
                              <div
                                key={conceptId}
                                className="group inline-flex items-center gap-1 rounded-full bg-white border border-gray-300 px-2 py-1 transition-all hover:shadow-md"
                              >
                                {/* Order 뱃지 */}
                                <span 
                                  className="inline-flex items-center justify-center font-semibold text-gray-700"
                                  style={{
                                    backgroundColor: 'rgba(156, 163, 175, 0.3)',
                                    borderRadius: '4px',
                                    width: '24px',
                                    height: '16px',
                                    minWidth: '24px',
                                    fontSize: '9px'
                                  }}
                                >
                                  {concept.order || concept.id}
                                </span>
                                
                                {/* Concept Type 뱃지 */}
                                {(() => {
                                  // concept_type이 있으면 직접 사용, 없으면 text에서 추출 시도
                                  const conceptType = concept.concept_type || 
                                    (concept.text && concept.text.includes(':') ? 
                                      concept.text.split(':')[0].split('-')[1] : null);
                                  
                                  return conceptType ? (
                                    <span 
                                      className="inline-flex items-center justify-center font-semibold text-gray-800"
                                      style={{
                                        backgroundColor: getConceptTypeColor(conceptType),
                                        borderRadius: '4px',
                                        width: '24px',
                                        height: '16px',
                                        minWidth: '24px',
                                        fontSize: '8px'
                                      }}
                                      title={getConceptTypeName(conceptType)}
                                    >
                                      {conceptType}
                                    </span>
                                  ) : null;
                                })()}
                                
                                <span 
                                  className="concept-tag-text text-xs text-gray-700"
                                  dangerouslySetInnerHTML={{ 
                                    __html: renderLatex(
                                      // text에 타입이 포함되어 있으면 제거 (예: "AL-DEF: 약수와 배수" → "약수와 배수")
                                      concept.text.includes(':') ? 
                                        concept.text.split(':').slice(1).join(':').trim() : 
                                        concept.text
                                    ) 
                                  }}
                                />
                                <button
                                  onClick={() => toggleConcept(conceptId)}
                                  className="ml-1 rounded-full p-0.5 text-gray-500 transition-colors hover:bg-gray-200 hover:text-gray-700"
                                  title="제거"
                                >
                                  <X className="size-3" />
                                </button>
                              </div>
                            );
                          })}
                        </div>
                        {selectedConcepts.length >= 10 && (
                          <p className="mt-2 text-xs text-orange-600">
                            최대 10개까지만 선택 가능합니다.
                          </p>
                        )}
                      </div>
                    )}
                  </div>
                  
                  <div className="min-h-0 flex-1 overflow-hidden">
                    <div className="overflow-y-auto rounded border p-4" style={{ height: '200px' }}>
                      {filteredConcepts.length > 0 ? (
                        <div className="space-y-2">
                          {filteredConcepts.map(concept => {
                            const isSelected = selectedConcepts.includes(concept.id);
                            
                            return (
                              <div
                                key={concept.id}
                                onClick={() => toggleConcept(concept.id)}
                                className={`flex cursor-pointer items-center gap-2 rounded-lg p-2 transition-all duration-200 ${
                                  isSelected 
                                    ? 'bg-primary/10 hover:bg-primary/20 border border-primary/30' 
                                    : 'hover:bg-gray-50 border border-transparent'
                                }`}
                              >
                                {/* Order 뱃지 */}
                                <span 
                                  className="inline-flex items-center justify-center font-semibold text-gray-700"
                                  style={{
                                    backgroundColor: 'rgba(156, 163, 175, 0.3)',
                                    borderRadius: '6px',
                                    width: '28px',
                                    height: '18px',
                                    minWidth: '28px',
                                    fontSize: '10px'
                                  }}
                                  title={`순서: ${concept.order || concept.id}`}
                                >
                                  {concept.order || concept.id}
                                </span>
                                
                                {/* Concept Type 뱃지 */}
                                {(() => {
                                  // concept_type이 있으면 직접 사용, 없으면 text에서 추출 시도
                                  const conceptType = concept.concept_type || 
                                    (concept.text && concept.text.includes(':') ? 
                                      concept.text.split(':')[0].split('-')[1] : null);
                                  
                                  return conceptType ? (
                                    <span 
                                      className="inline-flex items-center justify-center font-semibold text-gray-800"
                                      style={{
                                        backgroundColor: getConceptTypeColor(conceptType),
                                        borderRadius: '6px',
                                        width: '28px',
                                        height: '18px',
                                        minWidth: '28px',
                                        fontSize: '9px'
                                      }}
                                      title={getConceptTypeName(conceptType)}
                                    >
                                      {conceptType}
                                    </span>
                                  ) : null;
                                })()}
                                
                                {/* 개념 텍스트 */}
                                <span 
                                  className={`concept-text flex-1 text-xs lg:text-sm ${
                                    isSelected ? 'font-medium' : ''
                                  }`}
                                  dangerouslySetInnerHTML={{ 
                                    __html: renderLatex(
                                      // text에 타입이 포함되어 있으면 제거 (예: "AL-DEF: 약수와 배수" → "약수와 배수")
                                      concept.text.includes(':') ? 
                                        concept.text.split(':').slice(1).join(':').trim() : 
                                        concept.text
                                    ) 
                                  }}
                                />
                                
                                {/* 체크박스 (오른쪽 끝) */}
                                <input
                                  type="checkbox"
                                  checked={isSelected}
                                  onChange={(e) => {
                                    e.stopPropagation();
                                    toggleConcept(concept.id);
                                  }}
                                  className="shrink-0 rounded text-primary focus:ring-primary"
                                  onClick={(e) => e.stopPropagation()}
                                />
                              </div>
                            );
                          })}
                        </div>
                      ) : (
                        <div className="py-8 text-center text-gray-500">
                          {isLoadingMathConcepts ? '수학 개념을 불러오는 중...' : 
                            conceptSearch ? '검색 결과가 없습니다.' : '수학 개념이 없습니다.'}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* 오른쪽/하단: AI 지시사항 영역 */}
            <div className="flex w-full flex-col overflow-hidden border-t p-4 lg:w-1/3 lg:border-t-0 lg:p-6">
              <div className="flex flex-1 flex-col">
                <label className="mb-2 block text-xs font-medium text-gray-700 lg:text-sm">
                  AI에게 전달할 지시사항 <span className="text-red-500">*</span>
                </label>
                <textarea
                  value={userPrompt}
                  onChange={(e) => setUserPrompt(e.target.value)}
                  placeholder="예: 이 문제를 더 어렵게 변형해주세요 / 실생활 예시를 포함해주세요 / 그래프를 활용한 문제로 만들어주세요"
                  className="w-full resize-none rounded border p-3 text-sm"
                  style={{ height: '80px', maxHeight: '80px' }}
                  maxLength={500}
                />
                <div className="mt-2 text-xs text-gray-500 lg:text-sm">
                  {userPrompt.length}/500자
                </div>

                {/* 문제 분석 옵션 선택 (문제 분석 탭에서만 표시) */}
                {activeTab === 'prob_analysis' && (
                  <div className="mt-4">
                    <label className="mb-2 block text-xs font-medium text-gray-700 lg:text-sm">
                      분석 데이터 종류 선택
                    </label>
                    <div className="space-y-2 rounded border bg-gray-50 p-3">
                      {[
                        { key: 'solution', label: '풀이' },
                        { key: 'answer', label: '정답' },
                        { key: 'concept_ids', label: '개념 ID' },
                        { key: 'difficulty', label: '난이도' }
                      ].map(option => (
                        <label key={option.key} className="flex cursor-pointer items-center gap-2">
                          <input
                            type="checkbox"
                            checked={analysisOptions[option.key]}
                            onChange={(e) => 
                              setAnalysisOptions(prev => ({
                                ...prev,
                                [option.key]: e.target.checked
                              }))
                            }
                            className="rounded text-primary focus:ring-primary"
                          />
                          <span className="text-xs lg:text-sm">{option.label}</span>
                        </label>
                      ))}
                    </div>
                    <div className="mt-1 text-xs text-gray-500">
                      * 최소 하나 이상 선택해주세요
                    </div>
                  </div>
                )}
              </div>
              
              {/* 버튼들 */}
              <div className="mt-4 flex justify-end gap-2 border-t pt-4 lg:mt-6 lg:pt-6">
                <Button
                  variant="secondary"
                  onClick={handleClose}
                  disabled={isSubmitting}
                >
                  취소
                </Button>
                <Button
                  variant="primary"
                  onClick={handleSubmit}
                  disabled={isSubmitting}
                  loading={isSubmitting}
                  className="bg-primary hover:bg-[#3da575]"
                >
                  {activeTab === 'prob_analysis' ? '문제 분석 요청' : '문제 생성 요청'}
                </Button>
              </div>
            </div>
          </div>
        </div>
      </Modal>

      {/* 문제 미리보기 모달 */}
      <ProblemPreview
        isOpen={showPreview}
        onClose={() => setShowPreview(false)}
        problemIds={previewProblemIds}
        selectionMode={true}
        onSelectProblem={handlePreviewSelect}
      />

      <style jsx>{`
        .problem-content *:not(.katex):not(.katex *) {
          font-family: 'Pretendard', sans-serif !important;
          font-weight: 500 !important;
          font-size: 16px !important;
        }
        .problem-preview-content *:not(.katex):not(.katex *) {
          font-family: 'Pretendard', sans-serif !important;
          font-weight: 400 !important;
          font-size: 14px !important;
          line-height: 1.5 !important;
        }
        .problem-preview-content {
          position: relative;
        }
        .problem-preview-content p {
          margin: 0 0 8px 0;
        }
        .problem-preview-content p:last-child {
          margin-bottom: 0;
        }
        .katex {
          font-size: 1em !important;
        }
        .problem-preview-content .katex {
          font-size: 0.9em !important;
        }
        .PlaygroundEditorTheme__boxContent {
          border: 1px solid black;
          padding: 10px;
          margin: 10px auto;
          max-width: 100%;
          border-radius: 4px;
          background-color: #ffffff;
        }
        .concept-text .katex {
          font-size: 0.9em !important;
        }
        .concept-tag-text .katex {
          font-size: 0.85em !important;
        }
      `}</style>
    </>
  );
}

export default CreateProblemModal; 